"use client";
import React, { ReactNode, useState, useRef, useEffect } from "react";
import "./tooltip.scss";

type TooltipPosition = "top" | "bottom" | "left" | "right";

interface TooltipProps {
  children: ReactNode;
  label: ReactNode;
  opened?: boolean;
  defaultOpened?: boolean;
  position?: TooltipPosition;
  withArrow?: boolean;
  delay?: number;
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  label,
  opened,
  defaultOpened = false,
  position = "top",
  withArrow = true,
  delay = 0,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpened);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<any>({ top: 0, left: 0 });

  const isControlled = opened !== undefined;
  const visible = isControlled ? opened : isOpen;

  const showTooltip = () => {
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => setIsOpen(true), delay);
    } else {
      setIsOpen(true);
    }
  };

  const hideTooltip = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(false);
  };

  useEffect(() => {
    if (wrapperRef.current && boxRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const box = boxRef.current.getBoundingClientRect();
      let top = 0;
      let left: string | number = 0;
      switch (position) {
        case "top":
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
        case "bottom":
          top = rect.height + 8;
          left = rect.width / 2 - box.width / 2;
          break;
        case "left":
          top = rect.top + rect.height / 2;
          left = (rect.left - 8) * -1;
          break;
        case "right":
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
      }
      setCoords({ top, left });
    }
  }, [visible, position]);

  return (
    <div
      className="tooltip-wrapper"
      ref={wrapperRef}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {visible && (
        <div
          className={`tooltip-box tooltip-${position} ${className} p-x24`}
          style={{
            top: coords.top,
            left: coords.left,
          }}
          ref={boxRef}
        >
          {label}
          {withArrow && (
            <div className={`tooltip-arrow tooltip-arrow-${position}`} />
          )}
        </div>
      )}
    </div>
  );
};
